import { ShopifyProductDTO } from '@/api/dto/ShopifyProductDTO';
import { SearchQueryFilters } from '@/models/filters/SearchQueryFilters';
import { CrudModule, IModuleState } from '@/store/entityModules/CrudModule';
import { ShopifyProduct } from '@/models/entities/ShopifyProduct';
import { ActionTree, GetterTree } from 'vuex';
import { EntityType } from '@/store/entityModules/types';
import { ICustomOption, IFieldFilter, ISearchFilter } from '@/models/filters/Filters';
import { getEntityApiClient } from '@/api/utils/GetClient';
import { AlertColor, chooseErrorMessageData, dispatchAlert } from '@/utils/alerts';

class ShopifyProductModule extends CrudModule<ShopifyProduct, ShopifyProductDTO> {
  state: IModuleState<ShopifyProduct, ShopifyProductDTO> = {
    ...this.state,

    entityType: EntityType.SHOPIFY_PRODUCT,
    queryFilters: new SearchQueryFilters({}),
    customFilteredItems: [],
    customLoading: false,
    customFilters: new SearchQueryFilters({ page: 1, rowsPerPage: 20 }),
  };

  getters: GetterTree<IModuleState<ShopifyProduct, ShopifyProductDTO>, any> = {
    ...this.getters,

    getItem: state => (id: number) => {
      const item = state.items.find((i: ShopifyProduct) => i.id === id);
      return item || null;
    },

    customFilteredItems(state): ShopifyProduct[] | undefined {
      return state.customFilteredItems;
    },
  };

  actions: ActionTree<IModuleState<ShopifyProduct, ShopifyProductDTO>, any> = {
    ...this.actions,

    setCustomFiltersNextPage({ state }) {
      if (state.customFilters) state.customFilters.page += 1;
    },

    setCustomFilterField({ state }, filterFields: IFieldFilter) {
      if (state.customFilters) {
        state.customFilters.fieldFilter = filterFields;
        state.customFilters.page = 1;
      }
    },

    setCustomInitialValue({ state }, defaultValue: number[] | number) {
      if (defaultValue !== undefined && typeof defaultValue !== 'number')
        defaultValue = defaultValue?.filter(item => item !== undefined);

      if (state.customFilters) {
        if (
          (defaultValue !== undefined && typeof defaultValue === 'number') ||
          defaultValue?.length > 0
        ) {
          if (typeof defaultValue === 'number')
            state.customFilters.initialValue = [defaultValue];
          else state.customFilters.initialValue = defaultValue;
        } else state.customFilters.initialValue = undefined;
      }
    },

    setCustomSearchField({ state }, searchFields: ISearchFilter) {
      if (state.customFilters) {
        state.customFilters.searchFilter = searchFields;
        state.customFilters.page = 1;
      }
    },

    async fetchCustomFilteredItems(
      { state, commit, dispatch },
      {
        startFromPage,
        customOptions,
      }: { startFromPage?: number; customOptions?: ICustomOption[] } = {},
    ) {
      if (startFromPage && startFromPage === 1) state.customFilteredItems = [];

      if (state.customFilters && startFromPage) {
        state.customFilters.page = startFromPage;
      }

      commit('setCustomLoading', true);

      try {
        const apiClient = getEntityApiClient(state.entityType);
        const response = await apiClient.fetchItems(
          state.customFilters || new SearchQueryFilters({}),
          customOptions,
        );
        commit('setCustomFilteredItems', response.items);
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          const errorMessage = chooseErrorMessageData(
            {
              entityName: state.entityType,
              action: 'fetch',
            },
            err.message,
          );
          dispatchAlert(AlertColor.ERROR, errorMessage, dispatch);
        }
      } finally {
        commit('setCustomLoading', false);
      }
    },
  };
}

const shopifyProductsModule = new ShopifyProductModule();
export default shopifyProductsModule;
